import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { IonButton, IonIcon } from '@ionic/react';
import { bagHandleOutline, chevronDownOutline, chevronUpOutline, locationOutline } from 'ionicons/icons';
import moment from 'moment';
import Loading from '../../../components/spinner';
import { withTranslation } from '../../../lib/translate';
import Layout from '../../../components/layout';
import { NormalText, SmallText, Spacer, StrongText, Title } from '../../../components/common';
import NoData from '../../../components/noData';
import Incrementer from '../../../components/incrementer';
import Basket from '../../../lib/basket';
import { getEvent } from '../../../store/actions';
import { forwardTo } from '../../../lib/utils';
import './index.css';

class EventTickets extends React.Component {
  constructor(props) {
    super(props);
    // re-entering from the basket icon carries no navigation state, so fall back to the fetched event
    const requestedEventId = props.location?.state?.eventId;
    const storedEvent =
      requestedEventId != null && String(props.event?.id) !== String(requestedEventId)
        ? null
        : props.event;
    const event = props.location?.state?.event || storedEvent || null;
    this.state = {
      event,
      expandedTierId: this.getDefaultTierId(event),
    };
  }

  componentDidMount() {
    const { restaurants } = this.props
    Basket.setRestaurant(restaurants.find((restaurant) => restaurant.is_event_venue));
    if (!this.state.event) {
      // nothing in state or the store - use the requested event or rebuild from the basket
      const requestedEventId = this.props.location?.state?.eventId;
      const ticket = Basket.getItems().find((basketItem) => basketItem.item?.event_id);
      const eventId = requestedEventId != null ? requestedEventId : ticket?.item?.event_id;
      if (eventId != null) {
        this.props.dispatch(getEvent(eventId));
      }
    }
    this.expandTierFromBasket();
  }

  componentDidUpdate(prevProps) {
    if (this.props.event && prevProps.event !== this.props.event) {
      if (!this.state.event) {
        this.basketExpandHandled = false;
        this.setState(
          { event: this.props.event, expandedTierId: this.getDefaultTierId(this.props.event) },
          this.expandTierFromBasket,
        );
        return;
      }
      if (this.props.event.id === this.state.event.id) {
        // prices and availability can change while the user is picking tickets
        this.applyEventUpdate(this.props.event);
        return;
      }
    }
    if (prevProps.basketUpdated !== this.props.basketUpdated) {
      this.expandTierFromBasket();
      this.basketExpandHandled = true;
    }
  }

  applyEventUpdate = (event) => {
    const tiers = this.getActiveTiers(event);
    const expandedTierId = tiers.some((tier) => tier.id === this.state.expandedTierId)
      ? this.state.expandedTierId
      : this.getDefaultTierId(event);
    this.setState({ event, expandedTierId });
  };

  backHandler = () => {
    const event = this.state.event || this.props.event;
    if (event?.id) {
      forwardTo('/event-details', { id: event.id });
      return;
    }
    forwardTo('/events');
  };

  getDefaultTierId = (event) => {
    const tiers = this.getActiveTiers(event);
    const firstAvailable = tiers.find((tier) => !tier.is_sold_out) || tiers[0];
    return firstAvailable ? firstAvailable.id : null;
  };
  expandTierFromBasket = () => {
    if (this.basketExpandHandled || !this.state.event) {
      return;
    }
    const selected = this.getSelection();
    if (selected.length === 0) {
      return;
    }
    this.basketExpandHandled = true;
    this.setState({ expandedTierId: selected[0].tier.id });
  };

  getActiveTiers = (event) =>
    ((event && event.ticket_tiers) || [])
      .filter((tier) => tier.is_active)
      .sort((a, b) => a.sort_order - b.sort_order);

  getEventImage = (event) => {
    const images = event.images || [];
    return images.find((image) => image.is_primary) || images[0] || null;
  };

  getTierSubtitle = (tier) => (tier.features ? tier.features.split('\n')[0] : tier.description);

  getMaxQuantity = (tier) => {
    if (tier.is_sold_out) return 0;
    const perOrder = tier.max_per_order || 0;
    const available = tier.available_quantity != null ? tier.available_quantity : perOrder;
    return Math.min(perOrder, available);
  };

  getEventMaxQuantity = () => (this.state.event && this.state.event.max_tickets_per_order) || 0;

  // the event caps the whole order, so a tier can be limited before its own maximum is reached
  getQuantityLimit = (tier) => {
    const tierMax = this.getMaxQuantity(tier);
    const eventMax = this.getEventMaxQuantity();
    if (!eventMax) {
      return { max: tierMax, limitedByEvent: false };
    }
    const selected = this.getSelection().reduce((sum, item) => sum + item.quantity, 0);
    const remaining = Math.max(eventMax - (selected - this.getQuantity(tier)), 0);
    return { max: Math.min(tierMax, remaining), limitedByEvent: selected >= eventMax };
  };

  toggleTier = (tierId) =>
    this.setState((prev) => ({ expandedTierId: prev.expandedTierId === tierId ? null : tierId }));

  getTicketSku = (tier) => `event-${this.state.event.id}-tier-${tier.id}`;

  getBasketIndex = (tier) => {
    const sku = this.getTicketSku(tier);
    return Basket.getItems().findIndex((basketItem) => basketItem.item?.sku === sku);
  };

  getQuantity = (tier) => {
    const index = this.getBasketIndex(tier);
    return index === -1 ? 0 : Basket.getItems()[index].quantity;
  };

  constructBasketItem = (tier, quantity) => {
    const { event } = this.state;
    const image = this.getEventImage(event);
    return {
      item: {
        sku: this.getTicketSku(tier),
        productName: `${event.name} · ${tier.name}`,
        productPrice: (tier.price / 100).toFixed(2),
        description: tier.description,
        is_ticket: true,
        event_id: event.id,
        event_name: event.name,
        event_start_time: event.start_time,
        venue_name: event.venue_name,
        ticket_tier_id: tier.id,
        ticket_tier_name: tier.name,
        max_quantity: this.getMaxQuantity(tier),
        event_max_quantity: this.getEventMaxQuantity(),
        image: image ? image.image : null,
      },
      quantity,
      selectedChoices: [],
      instructions: '',
    };
  };

  setQuantity = (tier, value) => {
    const index = this.getBasketIndex(tier);
    if (index === -1) {
      if (value > 0) {
        Basket.addToBasket(this.constructBasketItem(tier, value));
      }
    } else {
      Basket.changeItemQuantity(index, value);
    }
  };

  getSelection = () =>
    this.getActiveTiers(this.state.event)
      .map((tier) => ({ tier, quantity: this.getQuantity(tier) }))
      .filter((item) => item.quantity > 0);

  goToCheckout = () => {
    const selection = this.getSelection();
    if (selection.length === 0) {
      return;
    }
    // the tickets are paid for through the normal order flow - purchaseEvent is
    // dispatched from the payment saga once the charge has gone through
    Basket.setDeliveryOption({ id: 'events', label: 'Events', route: '/event-tickets' });
    Basket.setCollectionTime(null);
    Basket.setServicePercentage(0);
    forwardTo('/event-checkout');
  };

  render() {
    const { __ } = this.props;
    const { event, expandedTierId } = this.state;

    if (!event) {
      return (
        <Loading>
          <Layout title={__('Select tickets')} headerWithTitle color="transparent" backHandler={this.backHandler} >
            <div className="absolute-content flex-row-wrapper event-tickets-empty">
              <NoData label="Event not found" />
            </div>
          </Layout>
        </Loading>
      );
    }

    const tiers = this.getActiveTiers(event);
    const image = this.getEventImage(event);
    const startTime = moment(event.start_time);
    const selection = this.getSelection();
    const totalCount = selection.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = selection.reduce(
      (sum, item) => sum + (item.quantity * item.tier.price) / 100,
      0,
    );
    const formattedTotal = Basket.formatPrice(totalPrice, true);

    return (
      <Loading>
        <Layout title={__('Select tickets')} headerWithTitle color="transparent" backHandler={this.backHandler} >
          <div
            className={`absolute-content event-tickets scrollable-y${totalCount > 0 ? ' has-footer' : ''}`}
          >
            <Title className={'web-only'}>{__('Select tickets')}</Title>

            <div className="event-tickets-summary box-wrapper">
              {image && (
                <div
                  className="event-tickets-summary-image"
                  style={{ backgroundImage: `url(${image.image})` }}
                />
              )}
              <div className="event-tickets-summary-info">
                <StrongText className="block">{event.name}</StrongText>
                <SmallText className="block ">{startTime.format('ddd MMMM D, YYYY')}</SmallText>
                <div className="event-tickets-summary-location">
                  <IonIcon icon={locationOutline} />
                  <SmallText className="">{event.venue_name}</SmallText>
                </div>
              </div>
            </div>
            <Spacer size={2} />

            <StrongText className="block">{__('Available tickets')}</StrongText>
            <Spacer size={1} />
            {tiers.map((tier) => {
              const expanded = expandedTierId === tier.id;
              const quantity = this.getQuantity(tier);
              const { max, limitedByEvent } = this.getQuantityLimit(tier);
              const isLimited = tier.availability_status === 'selling_fast';
              const subtitle = this.getTierSubtitle(tier);

              return (
                <div
                  key={`tier-${tier.id}`}
                  className={`event-tickets-card box-wrapper${expanded ? ' expanded' : ''}`}
                  onClick={() => this.toggleTier(tier.id)}
                >
                  <div className="event-tickets-card-header">
                    <div className="event-tickets-card-name">
                      <StrongText>{tier.name}</StrongText>
                      {tier.is_sold_out ? (
                        <span className="event-tickets-badge event-tickets-badge-muted">
                          {__('Sold out')}
                        </span>
                      ) : (
                        isLimited && <span className="event-tickets-badge">{__('LIMITED')}</span>
                      )}
                    </div>
                    <div className="event-tickets-card-price">
                      <StrongText>{Basket.formatPrice(tier.price / 100, true)}</StrongText>
                      <IonIcon icon={expanded ? chevronUpOutline : chevronDownOutline} />
                    </div>
                  </div>

                  {!expanded && subtitle && (
                    <SmallText className="block  event-tickets-card-subtitle">
                      {subtitle}
                    </SmallText>
                  )}

                  {expanded && (
                    <div className="event-tickets-card-body">
                      {tier.description && (
                        <SmallText className="block  event-tickets-card-desc">
                          {tier.description}
                        </SmallText>
                      )}
                      <div className="event-tickets-qty" onClick={(e) => e.stopPropagation()}>

                        <Incrementer
                          quantity={quantity}
                          step={1}
                          allowNegative={false}
                          minLimit={0}
                          maxLimit={max}
                          onUpdate={(val) => this.setQuantity(tier, val)}
                        />
                        {tier.is_sold_out ? (
                          <SmallText className="block  event-tickets-max">
                            {__('SOLD OUT')}
                          </SmallText>
                        ) : limitedByEvent ? (
                          <SmallText className="block  event-tickets-max">
                            {__('Max {count} per event', { count: this.getEventMaxQuantity() })}
                          </SmallText>
                        ) : (
                          this.getMaxQuantity(tier) > 0 && (
                            <SmallText className="block  event-tickets-max">
                              {__('Max {count} per person', {
                                count: this.getMaxQuantity(tier),
                              })}
                            </SmallText>
                          )
                        )}
                      </div>

                    </div>

                  )}
                </div>
              );
            })}
          </div>
          {totalCount > 0 && (
            <div className="event-tickets-footer">
              <div className="event-tickets-basket">
                <IonIcon color='primary' icon={bagHandleOutline} />
                {totalCount == 1 ?
                  <SmallText>
                    {__('{count} ticket · {price}', { count: totalCount, price: formattedTotal })}
                  </SmallText>
                  :

                  <SmallText>
                    {__('{count} tickets · {price}', { count: totalCount, price: formattedTotal })}
                  </SmallText>
                }

              </div>
              <IonButton
                expand="block"
                className="event-tickets-checkout-button"
                onClick={this.goToCheckout}
              >
                {__('Continue to Checkout · {price}', { price: formattedTotal })}
              </IonButton>
            </div>
          )}
        </Layout>
      </Loading>
    );
  }
}

const stateToProps = (store) => {
  return {
    basketUpdated: store.orders.basketUpdated,
    event: store.orders.event,
    profile: store.profile.profile,
    restaurants: store.restaurants.restaurants
  };
};

export default connect(stateToProps)(withRouter(withTranslation(EventTickets)));
